/* eslint-disable @typescript-eslint/ban-ts-comment */
/**
 * @file Maas360Estimator.tsx
 * Exports a top-level React component that handles routing, context, and i18n.
 */
import { useState, useEffect } from 'react';
import Helmet from 'react-helmet';
import './Maas360Estimator.scss';
import 'src/common/style/common.scss';
import {
  urxScriptLoader,
  urxScript,
} from 'src/common/hooks/widgets/globalDefinitions';
import { useBPName } from 'src/common/hooks/widgets/useBPName';
import { UT30Maas360 } from '../constants/default';
import Welcome from './Templates/Welcome/Welcome';
import Assessment, { topMatchProps } from './Templates/Assessment/Assessment';
import { swapCountryAndLanguage } from 'src/common/utils/swapCountryAndLanguage';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import { Modal } from 'carbon-components-react';
import {
  useLang,
  LangDefaults,
  determineCountry,
} from 'src/common/hooks/widgets/useLang';
import messages from '../locales/messages';
import { segmentsTracking } from 'src/common/lib/tracker';
import BusinessPartner from 'src/common/components/BusinessPartner/BusinessPartner';
import Thanks from './Molecules/Thanks/Thanks';
import { FormModal } from 'src/common/utils/FormModal';
import { useEnv } from 'src/common/hooks/widgets/useEnv';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import * as URX_FORM from '../constants/urx-form';
import { getParamsFromUrl } from 'src/common/utils/getParamsFromUrl';
import { useMaas360Selector } from '../lib/redux/hooks';
import { createUseTranslation } from 'src/common/hooks/widgets/useTranslation';
import { convertToString } from '../lib/utils';
import { shortcode } from '../Maas360Estimator.widget';

/**
 * App component.
 *
 * The top-level parent component that defines this application.
 *
 * @param {object} props Component props.
 * @param {Element} props.element The DOM element that contains this widget.
 */

export interface AppProps {
  element: Element;
  langCode: string;
  test: boolean;
  countryToTest: string;
}

export type que8ValueProps = {
  multi: {
    selectedItems: Array<OSAnswerType>;
  };
};

export type OSAnswerType = {
  text: string;
  cookieText: string;
};

export type QuesIDProps = {
  Q1?: string;
  Q2?: string;
  Q3?: string;
  Q4?: string;
  Q5?: string;
  Q6?: string;
  Q7?: string;
  Q8?: string;
};

const topMatchState = {
  title: '',
  plan: '',
  shortDesc: '',
  desc: '',
  icon: '',
  price: '',
  url: '',
  signUpUrl: '',
};

const Maas360Estimator = ({ langCode, test, countryToTest }: AppProps) => {
  const localeCode = swapCountryAndLanguage(langCode as IBMLocale);
  const { bpName, companyId, logo, desc } = useBPName(UT30Maas360, true);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [isRequired, setIsRequired] = useState(true);
  const [formComplete, setFormComplete] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [topMatchDetails, setTopMatchDetails] =
    useState<topMatchProps>(topMatchState);
  const [trackQuesID, setTrackQuesID] = useState<QuesIDProps>({
    Q1: '',
    Q2: '',
    Q3: '',
    Q4: '',
    Q5: '',
    Q6: '',
    Q7: '',
    Q8: '',
  });
  const [currency, setCurrency] = useState('');
  const [country, setCountry] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [lang, userLocale, , windowOpen] = useLang(
    null, // null since countryList to be fetch via COS
    test,
    countryToTest,
    setCurrency,
    setCountry,
    localeCode,
    shortcode, // widget short code to fetch countries via COS
  );

  const [que1Value, setQue1Value] = useState('');
  const [que2Value, setQue2Value] = useState('');
  const [que3Value, setQue3Value] = useState('');
  const [que4Value, setQue4Value] = useState('');
  const [que5Value, setQue5Value] = useState('');
  const [que6Value, setQue6Value] = useState('');
  const [que7Value, setQue7Value] = useState(0);
  const [que8Value, setQue8Value] = useState<que8ValueProps>({
    multi: {
      selectedItems: [],
    },
  });
  const [topMatch, setTopMatch] = useState('');
  const [showTopMatch, setShowTopMatch] = useState(false);
  const [noAdditionalReq, setNoAdditionalReq] = useState(false);
  const [deluxe, setDeluxe] = useState(false);
  const [model2, setModel2] = useState(false);
  // lang and userlocale is based out of ddo

  /* Have urx form deault in us-en when on language us page */
  const [urxlang] = useState(localeCode.toString());
  const [localCurrency, setlocalCurrency] = useState<string>(localeCode);
  const [toggleCountry, setToggleCountry] = useState(false);
  const [modalLocalCountry, setModalLocalCountry] = useState('');
  const [modalLocalCurrency, setModalLocalCurrency] = useState('USD');
  const [modalLocalLanguage, setModalLocalLanguage] = useState('English');
  const [localExist, setLocalExist] = useState(false);
  let localStorageFound = localStorage.getItem('langLocale');
  if (localStorageFound == null)
    localStorageFound = LangDefaults.localCountryLang;
  const [selectedLocale, setSelectedLocale] = useState(localStorageFound);

  // Determine Country
  determineCountry(
    setLocalExist,
    setSelectedLocale,
    setModalLocalCountry,
    setModalLocalLanguage,
    setModalLocalCurrency,
    setToggleCountry,
    lang,
    null, // null since countryList to be fetched via COS
    true,
    test,
    localeCode,
    shortcode, // widget short code to fetch countries via COS
  );

  // For auto-translations
  const AEMMessages = useMaas360Selector(
    (state) => state.translations.messages,
  );
  const useTranslation = createUseTranslation(messages, AEMMessages);

  useEffect(() => {
    if (noAdditionalReq) {
      setQue7Value(0);
      setQue8Value({
        multi: {
          selectedItems: [],
        },
      });
    }
  }, [noAdditionalReq]);

  useEffect(() => {
    setlocalCurrency(userLocale); // show local Currency
  }, [userLocale, localeCode, localCurrency]);

  const [environment] = useEnv();
  window.urxEnvironment = environment;
  window.onUrxFormSubmitSuccess = () => {
    setFormComplete(!formComplete);
    setOpen(false);
    setDrawerOpen(false);
  };

  const handleUrxForm = () => {
    initUrxForm(
      environment,
      URX_FORM.INSTANCE_ID,
      URX_FORM.FORM_ID,
      localeCode as IBMLocale,
      companyId,
    );
    setOpen(false);
    setDrawerOpen(true);
  };

  const handleLaunchModal = () => {
    setOpen(true);
    setStep(1);
    segmentsTracking('CTA Clicked', {
      pageTitle: 'MaaS360 Plan Assessment',
      object: 'Mass360 page',
      resultValue: 'CTA clicked',
      CTA: 'Launch Assessment',
      location: 'Assessment',
      action: '',
      field: '',
    });
  };

  const handleCloseModal = () => {
    setOpen(false);
    segmentsTracking('CTA Clicked', {
      pageTitle: 'MaaS360 Plan Assessment',
      object: 'Mass360 page',
      resultValue: 'CTA clicked',
      CTA: 'Closed assessment',
      location: 'Assessment',
      action: '',
      field: '',
    });
  };

  const reset = () => {
    setQue1Value('');
    setQue2Value('');
    setQue3Value('');
    setQue4Value('');
    setQue5Value('');
    setQue6Value('');
    setQue7Value(0);
    setQue8Value({
      multi: {
        selectedItems: [],
      },
    });
    setTopMatchDetails(topMatchState);
    setTrackQuesID({
      Q1: '',
      Q2: '',
      Q3: '',
      Q4: '',
      Q5: '',
      Q6: '',
      Q7: '',
      Q8: '',
    });
    setIsRequired(true);
    setStep(1);
    setTopMatch('');
    setShowTopMatch(false);
    setNoAdditionalReq(false);
    setDeluxe(false);
    segmentsTracking('CTA Clicked', {
      pageTitle: 'MaaS360 Plan Assessment',
      object: 'Mass360 page',
      resultValue: 'CTA clicked',
      CTA: 'Star Over',
      location: 'Assessment',
      action: '',
      field: '',
    });
  };

  useEffect(() => {
    if (getParamsFromUrl('open') === 'auto') {
      handleLaunchModal();
    }
  }, []);

  return (
    <div className="maas360_widget widget-styles">
      <Helmet>
        <script type="application/javascript" src={urxScriptLoader}></script>
        <script type="application/javascript" src={urxScript}></script>
      </Helmet>
      {!open && formComplete && (
        <Thanks bpName={bpName} useTranslation={useTranslation} />
      )}
      {!open && !formComplete && !drawerOpen ? (
        <>
          {bpName ? (
            <BusinessPartner bpName={bpName} logo={logo} desc={desc} />
          ) : null}
          <Welcome
            handleLaunchModal={handleLaunchModal}
            model2={model2}
            setModel2={setModel2}
            modalBtnLangText={modalLocalLanguage}
            localExist={localExist}
            toggleCountry={toggleCountry}
            flag={false} // to enable/disable globalization
            useTranslation={useTranslation}
          />
        </>
      ) : (
        <Assessment
          open={open}
          langCode={langCode}
          bpName={bpName}
          logo={logo}
          companyId={companyId}
          handleCloseModal={handleCloseModal}
          step={step}
          setStep={setStep}
          setOpen={setOpen}
          currentQuestion={currentQuestion}
          setCurrentQuestion={setCurrentQuestion}
          que1Value={que1Value}
          setQue1Value={setQue1Value}
          que2Value={que2Value}
          setQue2Value={setQue2Value}
          que3Value={que3Value}
          setQue3Value={setQue3Value}
          que4Value={que4Value}
          setQue4Value={setQue4Value}
          que5Value={que5Value}
          setQue5Value={setQue5Value}
          isRequired={isRequired}
          setIsRequired={setIsRequired}
          que6Value={que6Value}
          setQue6Value={setQue6Value}
          que7Value={que7Value}
          setQue7Value={setQue7Value}
          que8Value={que8Value}
          setQue8Value={setQue8Value}
          setTrackQuesID={setTrackQuesID}
          trackQuesID={trackQuesID}
          topMatch={topMatch}
          setTopMatch={setTopMatch}
          showTopMatch={showTopMatch}
          setShowTopMatch={setShowTopMatch}
          noAdditionalReq={noAdditionalReq}
          setNoAdditionalReq={setNoAdditionalReq}
          deluxe={deluxe}
          setDeluxe={setDeluxe}
          model2={model2}
          setModel2={setModel2}
          setTopMatchDetails={setTopMatchDetails}
          topMatchDetails={topMatchDetails}
          currency={currency}
          localeCode={urxlang}
          reset={reset}
          country={country}
          formComplete={formComplete}
          setFormComplete={setFormComplete}
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
          handleUrxForm={handleUrxForm}
          useTranslation={useTranslation}
          windowOpen={windowOpen ? windowOpen : window.open}
        />
      )}
      <div className="bx--row">
        <div
          className="bx--col-lg-4 bx--col-md-8 bx--col-sm-8 mar1 bx--offset-lg-12"
          id="countryModal"
        >
          <Modal
            open={model2}
            size="sm"
            preventCloseOnClickOutside={true}
            onRequestClose={() => {
              setModel2(false);
            }}
            modalLabel={`${convertToString(
              useTranslation('foundCountry', true),
            )} ${modalLocalCountry}. ${convertToString(
              useTranslation('accessPageCapitalized', true),
            )} ${modalLocalLanguage} (${modalLocalCurrency})`}
            modalHeading={`${convertToString(
              useTranslation('availableIn', true),
            )} ${modalLocalLanguage} ${convertToString(
              useTranslation('localCurrency_text', true),
            )} ${modalLocalCurrency}`}
            secondaryButtonText={convertToString(
              useTranslation('stayHere', true),
            )}
            primaryButtonText={convertToString(
              useTranslation('takeMeThere', true),
            )}
            onRequestSubmit={() => {
              localStorage.setItem('langLocale', selectedLocale);
              (window.location.href = window.location.href =
                'https://www.ibm.com/' +
                selectedLocale +
                '/products/maas360/pricing'),
                segmentsTracking('CTA Clicked', {
                  pageTitle: 'MaaS360 Plan Assessment',
                  object: 'Mass360 page',
                  resultValue: 'CTA clicked',
                  CTA: 'Switched to country ' + modalLocalCountry,
                  location: 'Assessment',
                  action: '',
                  field: '',
                });
              return true;
            }}
            onSecondarySubmit={() => {
              segmentsTracking('CTA Clicked', {
                pageTitle: 'MaaS360 Plan Assessment',
                object: 'Mass360 page',
                resultValue: 'CTA clicked',
                CTA: 'Stay here button clicked ' + modalLocalCountry,
                location: 'Assessment',
                action: '',
                field: '',
              });
              setToggleCountry(false);
              setModel2(false);
              return true;
            }}
          ></Modal>
        </div>
      </div>
      <FormModal
        pageTitle="MaaS360 Plan Assessment"
        object="Mass360 Page"
        productTitle={'MaaS360 Plan Assessment'}
        pageUrl={''}
        location={'Estimator'}
        bpName={bpName}
        logo={logo}
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        formId={URX_FORM.FORM_ID}
        lang={lang}
      ></FormModal>
    </div>
  );
};

export default Maas360Estimator;
